import { useEffect, useMemo, useState } from "react";
import { Check, Trash2, ClipboardList, ArrowUpRight, FileDown, X, Layers } from "lucide-react";
import { Page, Panel, DeleteButton, SearchInput, usePagination, Pagination } from "../lib/ui";
import { api, humanDate, money, today, openInvoiceFromWorkOrders, rowLink } from "../lib/api";

export default function WorkOrders({ user }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("beklemede");
  const [q, setQ] = useState("");
  const [pdfDate, setPdfDate] = useState(today());

  const load = async () => {
    const { data } = await api.get("/work-orders");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (status === "iptal") { if (!r.cancel_request) return false; }
      else if (status !== "all" && r.status !== status) return false;
      if (q) {
        const n = q.toLowerCase();
        return [r.customer_name, r.plate, r.chassis_no, r.job, r.note, r.created_by_name]
          .some((v) => (v || "").toLowerCase().includes(n));
      }
      return true;
    });
  }, [rows, status, q]);

  const pg = usePagination(filtered);
  const [sel, setSel] = useState([]);
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const processTogether = () => {
    const wos = rows.filter((r) => sel.includes(r.id));
    const names = new Set(wos.map((w) => (w.customer_name || "").toLocaleLowerCase("tr-TR").trim()));
    if (names.size > 1 && !window.confirm("Seçilen fişler farklı müşterilere ait. Fatura ekranında cariyi elle seçmeniz gerekecek. Devam edilsin mi?")) return;
    openInvoiceFromWorkOrders(wos);
  };
  const counts = {
    beklemede: rows.filter((r) => r.status === "beklemede").length,
    işlendi: rows.filter((r) => r.status === "işlendi").length,
    iptal: rows.filter((r) => r.cancel_request).length,
  };

  const markProcessed = async (wid) => {
    await api.post(`/work-orders/${wid}/mark-processed`);
    load();
  };
  const del = async (wid) => {
    if (!window.confirm("İş fişi silinsin mi?")) return;
    await api.delete(`/work-orders/${wid}`);
    load();
  };

  const downloadDailyPdf = async () => {
    try {
      const res = await api.get(`/work-orders/export/daily?date=${pdfDate}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `is-fisleri-${pdfDate}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 400);
    } catch (err) {
      alert(err.response?.data?.detail || "PDF indirilemedi");
    }
  };

  return (
    <>
      <Page
        eyebrow="İŞ TAKİP"
        title="İş Fişleri"
        subtitle={`Personelin saha fişleri. Beklemede olanları muhasebe inceler ve faturalandırır. (${counts.beklemede} beklemede)`}
        action={
          <div className="daily-pdf-controls" data-testid="daily-pdf-controls">
            <input type="date" data-testid="wo-daily-date" value={pdfDate} onChange={(e) => setPdfDate(e.target.value)} />
            <button className="primary-button" data-testid="wo-daily-pdf" onClick={downloadDailyPdf}>
              <FileDown size={14} /> Günlük PDF
            </button>
          </div>
        }
      />
      <Panel
        className="module-panel"
        action={<SearchInput value={q} onChange={setQ} testid="work-orders-search" placeholder="Müşteri, plaka, personel ara" />}
      >
        <div className="tab-row" data-testid="wo-tabs" style={{ marginBottom: 10 }}>
          {[
            ["beklemede", `Beklemede (${counts.beklemede})`],
            ["işlendi", `İşlendi (${counts.işlendi})`],
            ["iptal", `İptal talepleri (${counts.iptal})`],
            ["all", `Tümü (${rows.length})`],
          ].map(([k, l]) => (
            <button key={k} data-testid={`wo-tab-${k}`}
                    className={status === k ? "tab active" : "tab"}
                    onClick={() => setStatus(k)}>{l}</button>
          ))}
        </div>
        {sel.length > 0 && (
          <div data-testid="wo-bulk-bar" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10, padding: "8px 12px", background: "#eef6ff", borderRadius: 10 }}>
            <strong>{sel.length} fiş seçildi</strong>
            <button type="button" className="primary-button" data-testid="wo-process-together" onClick={processTogether}>
              <Layers size={14} /> Seçilenleri birlikte işle (tek fatura)
            </button>
            <button type="button" className="secondary-button" data-testid="wo-clear-selection" onClick={() => setSel([])}>Seçimi temizle</button>
          </div>
        )}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}></th><th>Tarih</th><th>Müşteri</th><th>Plaka / Şase</th><th>Kalemler</th>
                <th>Personel</th><th className="amount">Tutar</th><th>Durum</th><th></th>
              </tr>
            </thead>
            <tbody>
              {pg.slice.length === 0 && <tr><td colSpan="9" className="empty">İş fişi yok.</td></tr>}
              {pg.slice.map((w) => {
                const total = (w.items || []).reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_price) || 0), 0);
                return (
                  <tr key={w.id} data-testid={`wo-row-${w.id}`} className="clickable-row"
                      {...rowLink(`/is-fisi/${w.id}`)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" data-testid={`wo-select-${w.id}`} checked={sel.includes(w.id)} onChange={() => toggleSel(w.id)} />
                    </td>
                    <td>{humanDate(w.created_at)}</td>
                    <td><a href={`/is-fisi/${w.id}`} className="row-anchor" onClick={(e) => e.stopPropagation()}><strong>{w.customer_name}</strong></a></td>
                    <td>{w.no_vehicle || (!w.plate && !w.chassis_no) ? <span className="muted-cell">Araçsız</span> : (w.plate || "—")}<small>{w.chassis_no}</small></td>
                    <td><ClipboardList size={12} /> {[w.job, ...(w.items || []).map((it) => it.name)].filter(Boolean).join(", ") || "—"}</td>
                    <td>{w.created_by_name || "—"}</td>
                    <td className="amount">{money(total)}</td>
                    <td>
                      <span className={`status-pill ${w.status === "işlendi" ? "paid" : "pending"}`}>
                        {w.status === "işlendi" ? "İşlendi" : "Beklemede"}
                      </span>
                      {w.cancel_request && (
                        <span className="status-pill overdue" style={{ marginLeft: 6 }} data-testid={`wo-cancel-pill-${w.id}`}
                              title={`${w.cancel_request.by_name}: ${w.cancel_request.reason}`}>İptal talebi</span>
                      )}
                      {w.cancel_request && <small>{w.cancel_request.by_name}: {w.cancel_request.reason}</small>}
                    </td>
                    <td className="row-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="icon-button" title="Aç (orta tuş: yeni sekme)"
                              data-testid={`wo-open-${w.id}`}
                              {...rowLink(`/is-fisi/${w.id}`)}>
                        <ArrowUpRight size={14} />
                      </button>
                      {w.status !== "işlendi" && (
                        <button className="icon-button" title="İşlendi olarak işaretle"
                                data-testid={`wo-mark-${w.id}`}
                                onClick={() => markProcessed(w.id)}>
                          <Check size={14} />
                        </button>
                      )}
                      {user.role === "admin" && w.cancel_request && (
                        <button className="icon-button" title="İptal talebini reddet" data-testid={`wo-cancel-reject-${w.id}`}
                                onClick={() => api.post(`/work-orders/${w.id}/cancel-reject`).then(load).catch((e) => alert(e.response?.data?.detail || "Reddedilemedi"))}>
                          <X size={14} />
                        </button>
                      )}
                      {user.role === "admin" && (
                        <DeleteButton testid={`wo-delete-${w.id}`} onClick={() => del(w.id)} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total}
                      onChange={pg.setPage} testid="work-orders-pagination" />
        </div>
      </Panel>
    </>
  );
}
